import dynamic from "next/dynamic";
import Image from "next/image";
import { ReactNode, useEffect, useRef, useState } from "react";

import { usePerformance } from "@hooks";
import { WorkingArea } from "@utils";

const Spline = dynamic(
  () =>
    import("@baseComponents/SplineWrapper").then(mod => ({
      default: mod.SplineWrapper,
    })),
  {
    ssr: false,
    loading: () => <div />,
  },
);

import { SpeciazlizationColoredSpan } from "./Specializations.styled";

interface SpecializationInlineProps {
  splineCodeSrc: string;
  fallbackSrc: string;
  colorKey: WorkingArea;
  children?: ReactNode;
}

export const SpecializationInline = ({
  splineCodeSrc,
  colorKey,
  children,
  fallbackSrc,
}: SpecializationInlineProps) => {
  const { splineSupport } = usePerformance();

  const sharedStyle = {
    height: "20px",
    width: "20px",
    display: "inline-block",
    marginBottom: "-3px",
    marginLeft: "6px",
    marginRight: "6px",
  };

  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <span ref={containerRef} className="whitespace-nowrap">
      {splineSupport && isVisible ? (
        <Spline
          style={sharedStyle}
          scene={splineCodeSrc}
          onLoad={scene => scene.setZoom(1.5)}
        />
      ) : (
        <Image
          src={fallbackSrc}
          width={Number(sharedStyle.width.replace("px", ""))}
          height={Number(sharedStyle.height.replace("px", ""))}
          alt=""
          style={sharedStyle}
        />
      )}
      <SpeciazlizationColoredSpan colorKey={colorKey}>
        {children}
      </SpeciazlizationColoredSpan>
    </span>
  );
};
